import { useEffect, useState } from 'react';
import { FormikProps, useFormik } from 'formik';
import * as Yup from 'yup';
import { toast } from 'react-toastify';

import { useCreateOrgMutation } from 'store/apis/org.api';
import { useAuthLoginMutation } from 'store/apis/auth.api';

interface UseAuthSignupFormManagerProps {
  token?: string;
  email?: string;
  orgId?: number;
  role?: number;
  next?: (data?: any) => void;
}

export const useAuthSignupFormManager = ({
  token = '',
  email = '',
  orgId: organization_id,
  role,
  next = () => {},
}: UseAuthSignupFormManagerProps) => {
  const [
    createOrg,
    { isLoading: orgCreating, isSuccess: orgCreatingSuccess, isError: orgCreatingError, error: orgCreatingErrorData },
  ] = useCreateOrgMutation();
  const [login, { isLoading: isLoggingIn, isSuccess: loginSuccess, isError: loginError, error: loginErrorData }] =
    useAuthLoginMutation();
  const [loading_submit, setLoadingSubmit] = useState<boolean>(false);

  const initialValues = {
    first_name: '',
    last_name: '',
    company: '',
    email: email || '',
    phone: '',
    password: '',
  };

  const validationSchema = Yup.object({
    first_name: Yup.string().nullable().required('First name is required'),
    last_name: Yup.string().nullable().required('Last name is required'),
    company: token ? Yup.string().nullable() : Yup.string().required('Company name is required'),
    email: Yup.string().required('Email address is required').email('Invalid email address'),
    phone: Yup.string().required('Phone number is required'),
    password: Yup.string().required('Password is required').min(6, 'Password must be at least 6 characters'),
  });

  const formManager = useFormik({
    initialValues,
    validationSchema,
    onSubmit: (values) => {
      setLoadingSubmit(true);

      const { company, ...rest } = values;

      const org = token ? { ...rest, token, organization_id, role } : { company, ...rest };

      createOrg({ org });
    },
  });

  useEffect(() => {
    if (orgCreatingError) {
      toast.error((orgCreatingErrorData as any)?.data?.payload?.message);

      setLoadingSubmit(false);
    }

    if (!orgCreating && orgCreatingSuccess && !orgCreatingError) {
      const { email, password } = formManager.values;

      login({ email, password });
    }
  }, [orgCreating, orgCreatingSuccess, orgCreatingError]);

  useEffect(() => {
    if (loginError) {
      toast.error((loginErrorData as any)?.data?.payload?.message);

      setLoadingSubmit(false);
    }

    if (!isLoggingIn && loginSuccess && !loginError) {
      setLoadingSubmit(false);

      next();
    }
  }, [isLoggingIn, loginSuccess, loginError]);

  return [
    {
      formManager,
      loading_submit,
    },
  ] as [
    {
      formManager: FormikProps<typeof initialValues>;
      loading_submit: boolean;
    },
  ];
};
